<template>
  <div class="ranking-page">
    <div class="ranking-shell">
      <section class="ranking-hero">
        <div>
          <p class="eyebrow">RANKING</p>
          <h1>排行榜</h1>
          <p>按真实阅读与收藏数据排名的热门作品，发现大家都在追的漫画。</p>
        </div>
      </section>

      <div class="ranking-tabs">
        <button
          v-for="item in tabs"
          :key="item.type"
          type="button"
          :class="{ active: activeType === item.type }"
          @click="switchType(item.type)"
        >
          {{ item.name }}
        </button>
      </div>

      <div class="loading-state" v-if="loading">
        <div class="spinner"></div>
        <p>加载中...</p>
      </div>

      <div class="ranking-grid" v-else>
        <div
          class="ranking-card"
          v-for="(comic, index) in list"
          :key="comic.id"
          @click="goToComic(comic.id)"
        >
          <div class="rank-number" :class="{ top: index < 3 }">{{ String(index + 1).padStart(2, '0') }}</div>
          <div class="rank-cover">
            <img :src="getFullImageUrl(comic.cover)" :alt="comic.title" @error="handleImageError">
          </div>
          <div class="rank-info">
            <h3>{{ comic.title }}</h3>
            <p>{{ comic.author || '人气作品' }}</p>
            <small v-if="comic.heat !== undefined && activeType !== 'new'">🔥 {{ comic.heat }} 热度</small>
          </div>
        </div>

        <div class="empty-state" v-if="list.length === 0">
          暂无数据，先去读几本漫画吧。
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../api/index'
import request from '../api/request'
import { useRouter } from 'vue-router'
import comicPlaceholder from '@/assets/comic-placeholder.svg'
import { initYouthStore } from '../utils/youthContent'

export default {
  name: 'RankingPage',
  setup() {
    const router = useRouter()
    return { router }
  },
  data() {
    return {
      activeType: 'hot',
      list: [],
      loading: false,
      placeholderImage: comicPlaceholder,
      tabs: [
        { type: 'hot', name: '阅读热榜' },
        { type: 'favorite', name: '收藏榜' },
        { type: 'new', name: '新作榜' }
      ]
    }
  },
  created() {
    initYouthStore()
    this.fetchRanking()
  },
  methods: {
    async fetchRanking() {
      this.loading = true
      this.list = []
      try {
        const res = await api.getRanking(this.activeType, 30)
        if (res && res.code === 1) {
          this.list = Array.isArray(res.data) ? res.data : []
        } else if (res && res.code === 1001) {
          this.router.replace('/youth-protection')
        }
      } catch (error) {
        console.error('获取排行榜失败:', error)
      } finally {
        this.loading = false
      }
    },
    switchType(type) {
      if (this.activeType === type) return
      this.activeType = type
      this.fetchRanking()
    },
    goToComic(id) {
      if (id) {
        this.router.push(`/comic/${id}`)
      }
    },
    getFullImageUrl(path) {
      if (!path) return this.placeholderImage
      if (/^https?:\/\//i.test(path) || path.startsWith('data:') || path.startsWith('blob:')) return path
      if (path.startsWith('//')) return window.location.protocol + path
      if (path.startsWith('/')) return request.baseUrl + path
      return `${request.baseUrl}/${path}`
    },
    handleImageError(event) {
      const img = event && event.target
      if (!img || img.dataset.fallbackApplied) return
      img.dataset.fallbackApplied = '1'
      img.src = this.placeholderImage
    }
  }
}
</script>

<style scoped>
.ranking-page {
  min-height: 100vh;
  padding: 34px 0 64px;
  color: #eef3ff;
  background:
    radial-gradient(circle at 14% 0%, rgba(255, 204, 102, 0.11), transparent 32%),
    radial-gradient(circle at 86% 14%, rgba(35, 211, 238, 0.12), transparent 28%),
    linear-gradient(180deg, rgba(7, 9, 18, 0.2), rgba(7, 9, 18, 0.72));
}

.ranking-shell {
  width: min(1500px, 100%);
  margin: 0 auto;
  padding: 0 32px;
}

.ranking-hero {
  padding: 26px 28px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(180deg, rgba(20, 24, 39, 0.92), rgba(12, 15, 27, 0.94));
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.32);
}

.eyebrow {
  color: #23d3ee;
  font-size: 12px;
  font-weight: 900;
}

.ranking-hero h1 {
  margin: 8px 0 10px;
  color: #f5f7ff;
  font-size: clamp(30px, 3vw, 44px);
  line-height: 1.08;
}

.ranking-hero p {
  color: #9faaca;
}

.ranking-tabs {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin: 22px 0;
}

.ranking-tabs button {
  min-height: 42px;
  padding: 0 20px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  color: #dfe7ff;
  font-weight: 800;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.06);
}

.ranking-tabs button.active {
  color: #07101d;
  border: 0;
  background: linear-gradient(135deg, #ffcc66, #23d3ee);
}

.ranking-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 20px;
}

.ranking-card {
  position: relative;
  display: grid;
  gap: 10px;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(180deg, rgba(22, 26, 43, 0.96), rgba(11, 14, 25, 0.98));
  box-shadow: 0 22px 48px rgba(0, 0, 0, 0.28);
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.ranking-card:hover {
  transform: translateY(-3px);
  border-color: rgba(255, 204, 102, 0.34);
}

.rank-number {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  padding: 3px 8px;
  border-radius: 6px;
  color: #07101d;
  font-size: 13px;
  font-weight: 900;
  background: linear-gradient(135deg, #ffcc66, #23d3ee);
}

.rank-cover {
  height: 220px;
  overflow: hidden;
  border-radius: 8px;
  background: #070912;
}

.rank-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(1.05) contrast(1.04);
}

.rank-info h3 {
  color: #f5f7ff;
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rank-info p {
  margin-top: 4px;
  color: #9faaca;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rank-info small {
  display: inline-block;
  margin-top: 6px;
  color: #ffcc66;
  font-size: 12px;
}

.loading-state {
  display: grid;
  gap: 12px;
  justify-items: center;
  padding: 60px 0;
  color: #9faaca;
}

.spinner {
  width: 34px;
  height: 34px;
  border: 3px solid rgba(255, 255, 255, 0.15);
  border-top-color: #23d3ee;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state {
  grid-column: 1 / -1;
  padding: 40px;
  text-align: center;
  color: #9faaca;
}

@media (max-width: 1100px) {
  .ranking-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .ranking-shell {
    padding: 0 16px;
  }

  .ranking-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .rank-cover {
    height: 160px;
  }
}
</style>
